import { ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { ActionIcon, Button, Card, Grid, Group, MultiSelect, Select, Stack, Text, TextInput } from '@mantine/core';

import { deleteField, doc, updateDoc } from 'firebase/firestore';
import { ConfigDataType, TwilioContentType, WhatsappTemplateSettings, WhatsappTemplateTags } from '../../types';
import { AppConfigContext, ConfigContext } from '../../contexts';
import { useFirestore } from '../../helpers/firebaseContext';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconPlus, IconTrash } from '@tabler/icons-react';
import axios from 'axios';

const WhatsAppSettings = () => {
    const configContext = useContext(ConfigContext);
    const appConfigContext = useContext(AppConfigContext);

    const [whatsappTemplates, setWhatsappTemplates] = useState<ConfigDataType["whatsappTemplates"]>({})
    const [newTemplate, setNewTemplate] = useState<WhatsappTemplateSettings>({
        id: "",
        title: "",
        tags: [],
    })
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);
    const [twilioTemplates, setTwilioTemplates] = useState<TwilioContentType[]>([]);
    const [loadingTwilioTemplates, setLoadingTwilioTemplates] = useState(false);

    const db = useFirestore();

    useEffect(() => {
        if (configContext?.whatsappTemplates) {
            setWhatsappTemplates(configContext.whatsappTemplates);
        }
    }, [configContext, configContext?.whatsappTemplates]);

    useEffect(() => {
        const loadTwilioTemplates = async () => {
            const apiUrl = appConfigContext?.apiUrl;
            if (!apiUrl) return;
            setLoadingTwilioTemplates(true);
            try {
                const { data } = await axios.get(`${apiUrl}/getWhatsappTemplatesRequest`);
                const contents = data?.data?.contents || [];
                setTwilioTemplates(contents);
            } catch (e: any) {
                console.error("loadTwilioTemplates error", e);
                notifications.show({
                    title: "Error",
                    color: "red",
                    message: e?.message || "Failed to fetch WhatsApp templates",
                    icon: <IconAlertCircle size={16} />
                })
            } finally {
                setLoadingTwilioTemplates(false);
            }
        };
        loadTwilioTemplates();
    }, [appConfigContext?.apiUrl]);

    const tagOptions = useMemo(() => {
        const tags = new Set<string>();
        Object.values(WhatsappTemplateTags).forEach((tag) => tags.add(tag));
        Object.values(whatsappTemplates || {}).forEach((template) => {
            template.tags?.forEach((tag) => tags.add(tag));
        });
        return Array.from(tags).sort();
    }, [whatsappTemplates]);

    const handleUpdateField = (key: string, field: keyof WhatsappTemplateSettings, value: any) => {
        setWhatsappTemplates((prev) => {
            const previousValue = prev || {};
            return {
                ...previousValue,
                [key]: {
                    ...previousValue[key],
                    [field]: value,
                } as WhatsappTemplateSettings,
            }
        });
    };

    const handleDelete = (key: string) => {
        setWhatsappTemplates((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const handleSubmit = () => {
        setSaving(true);

        const configCollectionRef = doc(db, "config", "whatsappTemplates");

        const originalKeys = Object.keys(configContext?.whatsappTemplates || {});
        const currentKeys = Object.keys(whatsappTemplates || {});
        const removedKeys = originalKeys.filter((key) => !currentKeys.includes(key));

        const docData: any = {
            ...whatsappTemplates,
        };
        removedKeys.forEach((key) => {
            docData[key] = deleteField();
        });
        updateDoc(configCollectionRef, {
            ...docData
        }).then(() => {
            setSaving(false);

            notifications.show({
                title: "Success",
                color: "green",
                message: "Updated Successfully",
                icon: <IconCheck size={16} />
            })
        }).catch((e) => {
            setSaving(false);
            console.error("handleSubmit eeeeee =======>> ", e);

            notifications.show({
                title: "Error",
                color: "red",
                message: "Update Failed",
                icon: <IconAlertCircle size={16} />
            })

        });
    }

    const handleReset = () => {
        setWhatsappTemplates(configContext?.whatsappTemplates || {});
        setNewTemplate({
            id: "",
            title: "",
            templateContent: undefined,
            tags: [],
        });
    }

    // validate mandatory fields for new template ( tags should be mandatory ?)
    const canAdd = Boolean(newTemplate.templateContent?.sid?.trim());

    const handleAddTemplate = async () => {
        const id = newTemplate.templateContent?.sid?.trim() || "";
        if (!canAdd) {
            notifications.show({
                title: "Missing fields",
                color: "red",
                message: "Please select a template",
                icon: <IconAlertCircle size={16} />
            })
            return;
        }
        // make sure the entry key does not already exist 
        if (whatsappTemplates && whatsappTemplates[id]) {
            notifications.show({
                title: "Duplicate id",
                color: "red",
                message: "Template id already exists",
                icon: <IconAlertCircle size={16} />
            })
            return;
        }
        setAdding(true);
        try {
            const templateContent = newTemplate.templateContent;
            const resolvedTitle = newTemplate.title?.trim() || templateContent?.friendly_name || newTemplate.title;

            setWhatsappTemplates((prev) => ({
                ...prev,
                [id]: {
                    ...newTemplate,
                    id,
                    title: resolvedTitle,
                    templateContent,
                }
            }));
            setNewTemplate({
                id: "",
                title: "",
                templateContent: undefined,
                tags: [],
            });
        } catch (e: any) {
            console.error("add template error", e);
            notifications.show({
                title: "Error",
                color: "red",
                message: e?.message || "Failed to fetch template",
                icon: <IconAlertCircle size={16} />
            })
        } finally {
            setAdding(false);
        }
    };

    const renderTemplateFields = ({ //template fields so that it can be reused for both new template and existing templates
        template,
        onChange,
        action,
        disableId,
        showTemplateSelect,
    }: {
        template: WhatsappTemplateSettings;
        onChange: (field: keyof WhatsappTemplateSettings, value: any) => void;
        action?: ReactNode;
        disableId?: boolean;
        showTemplateSelect?: boolean;
    }) => {
        const resolvedTemplate = template.templateContent
            || twilioTemplates.find((item) => item.sid === template.id);
        const category = resolvedTemplate?.whatsappApproval?.category?.toLowerCase();
        const isMarketing = category === "marketing";
        const selectValue = resolvedTemplate?.sid || template.id || null;

        return (
        <Grid align="end">

            <Grid.Col span={{ lg: 4, xs: 12 }}>

                <Stack gap={4}>
                    {showTemplateSelect && (
                        <Select
                            label="Select Template"
                            placeholder={loadingTwilioTemplates ? "Loading..." : "Choose template"}
                            data={twilioTemplates.map((twilioTemplate) => ({
                                value: twilioTemplate.sid,
                                label: twilioTemplate.friendly_name || twilioTemplate.sid,
                            }))}
                            value={selectValue}
                            searchable
                            nothingFoundMessage="No templates"
                            onChange={(value) => {
                                if (disableId) return;
                                const selected = twilioTemplates.find((item) => item.sid === value);
                                if (value) {
                                    onChange("id", value);
                                }
                                if (!value) {
                                    onChange("id", "");
                                    onChange("templateContent", undefined);
                                }
                                if (selected) {
                                    onChange("templateContent", selected as any);
                                    if (!template.title?.trim()) {
                                        onChange("title", selected.friendly_name || template.title);
                                    }
                                }
                            }}
                            disabled={loadingTwilioTemplates || disableId}
                            comboboxProps={{ withinPortal: true, position: "bottom" }}
                        />
                    )}
                    {isMarketing && (
                        <Text size="xs" c="red" fw={600}>
                            Marketing template — may not reach all recipients.
                        </Text>
                    )}
                </Stack>


            </Grid.Col>

            <Grid.Col span={{ lg: 4, xs: 12 }}>
                <TextInput
                    label="Title"
                    placeholder="Title"
                    value={template.title}
                    onChange={(event) => onChange("title", event.currentTarget.value)}
                />
            </Grid.Col>


            <Grid.Col span={{ lg: 2, xs: 12 }}>
                <MultiSelect
                    label="Tags"
                    placeholder="Select tags"
                    searchable
                    data={tagOptions}
                    value={template.tags as string[]}
                    onChange={(value) => onChange("tags", value as any)}
                    comboboxProps={{ withinPortal: true, position: "bottom" }}
                />
            </Grid.Col>
            <Grid.Col span={{ lg: 1, xs: 12 }}>
                {action && (
                    <Group justify="flex-end">
                        {action}
                    </Group>
                )}
            </Grid.Col>
        </Grid>
        );
    };

    return (
        <div>

            <Card shadow="sm" p="xl" radius="md" withBorder>
                <Grid
                    justify='flex-start'
                    ta='center'
                >
                    <Grid.Col span={{ xs: 12 }}>
                        <Stack gap="sm">
                            <Text fw={600}>Add Template</Text>
                            <Card withBorder radius="md" p="md" bg={"blue.0"}>
                                {renderTemplateFields({
                                    template: newTemplate,
                                    onChange: (field, value) =>
                                        setNewTemplate((prev) => ({ ...prev, [field]: value })),
                                    action: (
                                        <Button leftSection={<IconPlus size={16} />} onClick={handleAddTemplate} loading={adding}>
                                            Add
                                        </Button>
                                    ),
                                    showTemplateSelect: true,
                                })}
                            </Card>
                        </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ xs: 12 }}>
                        <Stack gap="md">
                            <Text fw={600}>Existing Templates</Text>
                            {Object.keys(whatsappTemplates || {}).length === 0 && (
                                <Text c="dimmed">No templates found.</Text>
                            )}
                            {Object.entries(whatsappTemplates || {})
                                .sort(([a], [b]) => a.localeCompare(b)) // sorting by key so it doesn't shuffle around on edit
                                .map(([key, template]) => (
                                    <Card key={key} withBorder radius="md" p="md" bg={"green.0"}>
                                        {renderTemplateFields({
                                            template: {
                                                id: template.id || key,
                                                title: template.title || "",
                                                templateContent: template.templateContent,
                                                tags: template.tags || [],
                                            },
                                            onChange: (field, value) => handleUpdateField(key, field, value),
                                            action: (
                                                <ActionIcon color="red" variant="light" onClick={() => handleDelete(key)}>
                                                    <IconTrash size={16} />
                                                </ActionIcon>
                                            ),
                                            disableId: true,
                                            showTemplateSelect: true,
                                        })}
                                    </Card>
                                ))}
                        </Stack>
                    </Grid.Col>

                    <Grid.Col span={{ xs: 12 }}>
                        <Group justify="flex-end">
                            <Button

                                color="red"
                                onClick={handleReset}>
                                Cancel
                            </Button>
                            <Button color="green" onClick={handleSubmit} loading={saving}>
                                Save Changes
                            </Button>
                        </Group>
                    </Grid.Col>

                </Grid>
            </Card >

        </div >
    )
}

export default WhatsAppSettings
